import DataTable, {
  IDataTableRow,
  IHeadCell,
} from "components/Common/DataTable";

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  Paper,
  TableCell,
  Typography,
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAppDispatch, useAppSelector } from "app/hooks";
import TemplateToolbar from "../common/TemplateToolbar";
import MessageDetail from "../common/message-detail/MessageDetail";
import {
  loadZnsTemplates,
  syncZnsTemplates,
  clearSyncSuccess,
  selectZnsTemplatesResults,
  selectZnsTemplatesTotalCount,
  selectZnsTemplatesLoading,
  selectZnsTemplatesError,
  selectZnsTemplatesSyncLoading,
  selectZnsTemplatesSyncSuccess,
  selectZnsTemplatesSyncError,
  selectZnsTemplatesLastPulledDate,
} from "../store/znsTemplateSlice";
import {
  IZnsTemplate,
  ZnsTemplateStatus,
  ZnsTemplateTag,
} from "../types/znsTemplate";
import type { Order } from "components/Common/DataTable";
import Loading from "components/Common/Loading";
import moment from "moment";
import { toastEmitter } from "utils/toast.util";

const DATE_DISPLAY_FORMAT = "DD/MM/YYYY HH:mm";

/**
 * Formats an ISO date string to DD/MM/YYYY HH:mm for display.
 */
function formatDate(value: string | undefined): string {
  if (!value) return "";
  const m = moment.utc(value).local();
  return m.isValid() ? m.format(DATE_DISPLAY_FORMAT) : value;
}

/** Filter state for the ZNS template list (search, tag, status, date ranges, sort). */
export interface TemplateListFilters {
  tag: "" | ZnsTemplateTag;
  status: "" | ZnsTemplateStatus;
  createdDateFrom: string;
  createdDateTo: string;
  pulledDateFrom: string;
  pulledDateTo: string;
  sortBy: string;
  sortDirection: Order;
  searchTerm: string;
}

/** Default filter values when resetting or initializing. */
const DEFAULT_FILTERS: TemplateListFilters = {
  tag: "",
  status: "",
  createdDateFrom: "",
  createdDateTo: "",
  pulledDateFrom: "",
  pulledDateTo: "",
  sortBy: "",
  sortDirection: "asc",
  searchTerm: "",
};

/** Maps table column id to API SortBy value. Only columns in this map are sent as sort to the API. */
const SORT_FIELD_TO_API: Record<string, string> = {
  templateId: "TemplateId",
  templateName: "TemplateName",
  templateTag: "TemplateTag",
  status: "Status",
  createdDate: "CreatedDate",
  pulledDate: "ModifiedDate",
};

const headCells: IHeadCell[] = [
  {
    id: "templateId",
    numeric: false,
    disablePadding: false,
    label: "ZNS ID",
    sortable: true,
    minWidth: 160,
  },
  {
    id: "templateName",
    numeric: false,
    disablePadding: false,
    label: "Name",
    sortable: true,
  },
  {
    id: "templateTag",
    numeric: false,
    disablePadding: false,
    label: "ZNS Purpose",
    sortable: true,
    minWidth: 160,
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Status",
    sortable: true,
    minWidth: 160,
  },
  {
    id: "createdDate",
    numeric: false,
    disablePadding: false,
    label: "Creation Date",
    sortable: true,
  },
  {
    id: "pulledDate",
    numeric: false,
    disablePadding: false,
    label: "Pulled Date",
    sortable: true,
  },
  {
    id: "priceSdt",
    numeric: false,
    disablePadding: false,
    label: "Price",
    sortable: false,
  },
  {
    id: "actions",
    numeric: true,
    disablePadding: false,
    label: "",
    sortable: false,
  },
];

function TemplateRow(props: IDataTableRow) {
  const item = props.item as IZnsTemplate | undefined;
  if (!item) return null;
  return (
    <>
      <TableCell
        component="th"
        id={props.labelId}
        scope="row"
        sx={{ maxWidth: "250px", wordBreak: "nowrap" }}
      >
        {item.templateId}
      </TableCell>
      <TableCell>{item.templateName}</TableCell>
      <TableCell>{item.templateTag}</TableCell>
      <TableCell>{item.status}</TableCell>
      <TableCell>{formatDate(item.createdDate)}</TableCell>
      <TableCell>{formatDate(item.pulledDate)}</TableCell>
      <TableCell>{item.priceSdt}đ / ZNS</TableCell>
      <TableCell>
        <Button
          variant="text"
          color="primary"
          sx={{ fontSize: 15 }}
          onClick={() => props.onViewDetail?.(item)}
        >
          <VisibilityIcon />
        </Button>
      </TableCell>
    </>
  );
}

const DEFAULT_LIMIT = 15;

/**
 * Fetches ZNS templates from GET /api/ZnsTemplate/v1 via saga and renders them in a data table
 * with pagination, view detail, and sync actions.
 */
const FetchMessageTemplatePage = () => {
  const dispatch = useAppDispatch();
  const results = useAppSelector(selectZnsTemplatesResults);
  const totalCount = useAppSelector(selectZnsTemplatesTotalCount);
  const loading = useAppSelector(selectZnsTemplatesLoading);
  const error = useAppSelector(selectZnsTemplatesError);
  const syncLoading = useAppSelector(selectZnsTemplatesSyncLoading);
  const syncSuccess = useAppSelector(selectZnsTemplatesSyncSuccess);
  const syncError = useAppSelector(selectZnsTemplatesSyncError);
  const lastPulledDate = useAppSelector(selectZnsTemplatesLastPulledDate);

  const [paging, setPagingData] = useState({
    total: 0,
    pages: 1,
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const [filters, setFilters] = useState<TemplateListFilters>(DEFAULT_FILTERS);

  const [open, setOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IZnsTemplate | null>(
    null,
  );

  const getZnsTemplates = () => {
    const pageNumber = Math.max(1, paging.page);
    dispatch(
      loadZnsTemplates({
        PageNumber: pageNumber,
        PageSize: paging.limit,
        ...(filters.tag ? { Tag: filters.tag } : {}),
        ...(filters.status ? { Status: filters.status } : {}),
        ...(filters.createdDateFrom
          ? { CreatedDateFrom: filters.createdDateFrom }
          : {}),
        ...(filters.createdDateTo
          ? { CreatedDateTo: filters.createdDateTo }
          : {}),
        ...(filters.pulledDateFrom
          ? { PulledDateFrom: filters.pulledDateFrom }
          : {}),
        ...(filters.pulledDateTo ? { PulledDateTo: filters.pulledDateTo } : {}),
        ...(filters.sortBy && SORT_FIELD_TO_API[filters.sortBy]
          ? {
              SortBy: SORT_FIELD_TO_API[filters.sortBy],
              SortDirection: filters.sortDirection,
            }
          : {}),
        ...(filters.searchTerm ? { SearchTerm: filters.searchTerm } : {}),
      }),
    );
  };

  useEffect(() => {
    getZnsTemplates();
  }, [
    paging.page,
    filters.tag,
    filters.status,
    filters.createdDateFrom,
    filters.createdDateTo,
    filters.pulledDateFrom,
    filters.pulledDateTo,
    filters.sortBy,
    filters.sortDirection,
    filters.searchTerm,
  ]);

  useEffect(() => {
    setPagingData((prev) => {
      // Only go back a page when we have data and current page is empty (e.g. items deleted).
      // When loading, data is cleared so totalCount=0 and results=[] - do not reset page then.
      const nextPage =
        totalCount > 0 && results.length === 0 && prev.page > 1
          ? prev.page - 1
          : prev.page;
      return {
        ...prev,
        total: totalCount,
        pages: Math.ceil(totalCount / prev.limit) || 1,
        page: Math.max(1, nextPage),
      };
    });
  }, [totalCount, results.length]);

  useEffect(() => {
    if (syncSuccess) {
      toastEmitter.show(
        "Sync job has started, wait for the job to complete.",
        "info",
      );
      dispatch(clearSyncSuccess());
    }
  }, [syncSuccess, dispatch]);

  /** TablePagination uses 0-based page (0 = first page); we store 1-based for the API. */
  const onPageChange = (pageIndexZeroBased: number) => {
    setPagingData((prev) => ({ ...prev, page: pageIndexZeroBased + 1 }));
  };

  const handleTagChange = (tag: "" | ZnsTemplateTag) => {
    setFilters((prev) => ({ ...prev, tag }));
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusChange = (status: "" | ZnsTemplateStatus) => {
    setFilters((prev) => ({ ...prev, status }));
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateRangeChange = (from: string, to: string) => {
    setFilters((prev) => ({
      ...prev,
      createdDateFrom: from,
      createdDateTo: to,
    }));
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handlePulledDateRangeChange = (from: string, to: string) => {
    setFilters((prev) => ({
      ...prev,
      pulledDateFrom: from,
      pulledDateTo: to,
    }));
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handleSortChange = (property: string, direction: Order) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: property,
      sortDirection: direction,
    }));
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (name: string) => {
    setFilters((prev) => ({ ...prev, searchTerm: name }));
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPagingData((prev) => ({ ...prev, page: 1 }));
  };

  const onViewDetail = (data: IZnsTemplate) => {
    setSelectedTemplate(data);
    setOpen(true);
  };

  const handleSyncFromOA = () => {
    dispatch(syncZnsTemplates());
  };

  return (
    <Container maxWidth={false}>
      <Box>
        <Paper
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Typography variant="h6">
            Last Updated: {formatDate(lastPulledDate)}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: 15 }}
            disabled={syncLoading}
            onClick={handleSyncFromOA}
          >
            <CachedIcon sx={{ fontSize: 22, mr: 1 }} />
            {syncLoading ? "Syncing…" : "Sync from Zalo OA"}
          </Button>
        </Paper>
      </Box>
      <Box sx={{ width: "100%", mt: 3 }}>
        {(error || syncError) && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error?.Message ??
              syncError?.Message ??
              "Failed to load ZNS templates"}
          </Typography>
        )}
        <Paper>
          <TemplateToolbar
            tag={filters.tag}
            onTagChange={handleTagChange}
            status={filters.status}
            onStatusChange={handleStatusChange}
            dateRange={{
              from: filters.createdDateFrom,
              to: filters.createdDateTo,
            }}
            onDateRangeChange={handleDateRangeChange}
            pulledDateRange={{
              from: filters.pulledDateFrom,
              to: filters.pulledDateTo,
            }}
            onPulledDateRangeChange={handlePulledDateRangeChange}
            search={filters.searchTerm}
            onSearchChange={handleSearchChange}
            onReset={handleResetFilters}
          />
          {loading ? (
            <Loading />
          ) : (
            <DataTable
              hideToolbar={true}
              fromPage="notification"
              headCells={headCells}
              rows={results}
              paging={{ ...paging, page: paging.page - 1 }}
              name="templateId"
              title=""
              sortable={true}
              order={filters.sortDirection}
              orderBy={filters.sortBy}
              onSortChange={handleSortChange}
              expandablePagination={true}
              onPageChange={onPageChange}
              onRowsPerPageChange={() => {}}
              onViewDetail={onViewDetail}
            >
              <TemplateRow />
            </DataTable>
          )}
        </Paper>
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { maxHeight: "90vh" },
        }}
      >
        <Box
          sx={{
            height: "75vh",
            display: "flex",
            flexDirection: "column",
            p: 2,
          }}
        >
          {selectedTemplate && (
            <MessageDetail
              template={selectedTemplate}
              onClose={() => setOpen(false)}
            />
          )}
        </Box>
      </Dialog>
    </Container>
  );
};

export default React.memo(FetchMessageTemplatePage);
